import { call, put, takeLatest } from "redux-saga/effects";
import axios, { AxiosError, AxiosResponse } from "axios";

import { errorToast } from "data-handler/ducks/toast";
import { toastFormattedMessages } from "SCConstants";
import { RootState } from "data-handler/rootReducer";

export type GenderBreakdown = {
  male: number;
  female: number;
  total: number;
};

export type ExpenseBreakdown = {
  food_cost: number;
  other_cost: number;
  total: number;
};

export type CommodityPriceVarianceEntry = {
  commodity_id: number;
  commodity_name: string;
  distinct_prices?: number[];
  average_price: number;
  year_month?: string;
  month?: string;
  school_name?: string;
  purchase_date?: string;
  price_per_kg?: number;
  purchased_total_quantity?: number;
  total_amount_paid?: number;
  schools?: {
    school_name?: string;
    purchased_quantity?: number;
  }[];
  purchase_quantity?: number;
  quantity_purchased?: number;
};

export type DashboardSummaryEntry = {
  country_id: number;
  country_name: string;
  school_year?: string;
  year_month?: string;
  month?: string;
  enrollees_latest: GenderBreakdown;
  newcomers: GenderBreakdown;
  student_attendance?: GenderBreakdown;
  attendees_average: GenderBreakdown;
  beneficiaries: GenderBreakdown;
  beneficiaries_average?: GenderBreakdown;
  approved_reports_rate?: number;
  average_ration_per_student?: number;
  kg_consumed_total?: number;
  kg_consumed_total_wfp?: number;
  kg_consumed_total_non_wfp?: number;
  kg_delivered_total?: number;
  kg_purchased_total?: number;
  meals_served?: number;
  contributions_served?: number;
  expenses?: ExpenseBreakdown;
  absentee_rate: number;
  class_days: number;
  meal_days: number;
  feeding_days_rate: number;
  feeding_days_rate_per_school_average?: number;
  filled_school_days_rate?: number;
  canteen_attendance_rate?: number;
  school_canteen_functioning_average?: number;
  commodity_price_variance?: CommodityPriceVarianceEntry[];
  active_schools: number;
  inactive_schools?: number;
};

type State = {
  fetching: boolean;
  schoolYearsFetching: boolean;
  error: AxiosError | null;
  dashboardSummary: DashboardSummaryEntry[];
  dashboardMatched: number | null;
  dashboardSummaryLastSuccessKey: string | null;
  schoolYears: string[];
};

type DashboardSummaryResponse =
  | DashboardSummaryEntry[]
  | {
      results?: DashboardSummaryEntry[];
      summary?: DashboardSummaryEntry[];
      data?: DashboardSummaryEntry[];
      items?: DashboardSummaryEntry[];
      entries?: DashboardSummaryEntry[];
    };

const FETCH_DASHBOARD_SUMMARY_REQUEST =
  "schoolconnect/analytics/FETCH_DASHBOARD_SUMMARY_REQUEST";
const FETCH_DASHBOARD_SUMMARY_SUCCESS =
  "schoolconnect/analytics/FETCH_DASHBOARD_SUMMARY_SUCCESS";
const FETCH_DASHBOARD_SUMMARY_FAILURE =
  "schoolconnect/analytics/FETCH_DASHBOARD_SUMMARY_FAILURE";
const FETCH_SCHOOL_YEARS_REQUEST =
  "schoolconnect/analytics/FETCH_SCHOOL_YEARS_REQUEST";
const FETCH_SCHOOL_YEARS_SUCCESS =
  "schoolconnect/analytics/FETCH_SCHOOL_YEARS_SUCCESS";
const FETCH_SCHOOL_YEARS_FAILURE =
  "schoolconnect/analytics/FETCH_SCHOOL_YEARS_FAILURE";

type FetchDashboardSummaryRequestAction = {
  type: typeof FETCH_DASHBOARD_SUMMARY_REQUEST;
  countryId: number;
  schoolId?: string;
  schoolYear?: string;
  ongoingSchoolYear?: boolean;
  yearMonth?: string;
  adm1?: string;
  adm2?: string;
  adm3?: string;
  adm4?: string;
};

type FetchDashboardSummarySuccessAction = {
  type: typeof FETCH_DASHBOARD_SUMMARY_SUCCESS;
  data: DashboardSummaryEntry[];
  requestKey: string;
};

type FetchDashboardSummaryFailureAction = {
  type: typeof FETCH_DASHBOARD_SUMMARY_FAILURE;
  error: AxiosError;
};

type FetchSchoolYearsRequestAction = {
  type: typeof FETCH_SCHOOL_YEARS_REQUEST;
  countryId: number;
};

type FetchSchoolYearsSuccessAction = {
  type: typeof FETCH_SCHOOL_YEARS_SUCCESS;
  data: string[];
};

type FetchSchoolYearsFailureAction = {
  type: typeof FETCH_SCHOOL_YEARS_FAILURE;
  error: AxiosError;
};

const normalizeDashboardSummaryResponse = (
  payload: DashboardSummaryResponse
) => {
  if (Array.isArray(payload)) {
    return {
      data: payload,
    };
  }

  if (!payload || typeof payload !== "object") {
    return {
      data: [],
    };
  }

  const dataSource =
    payload.results ||
    payload.summary ||
    payload.data ||
    payload.items ||
    payload.entries ||
    ([] as DashboardSummaryEntry[]);
  return {
    data: Array.isArray(dataSource) ? dataSource : [],
  };
};

type Action =
  | FetchDashboardSummaryRequestAction
  | FetchDashboardSummarySuccessAction
  | FetchDashboardSummaryFailureAction
  | FetchSchoolYearsRequestAction
  | FetchSchoolYearsSuccessAction
  | FetchSchoolYearsFailureAction;

const initialState: State = {
  fetching: false,
  schoolYearsFetching: false,
  error: null,
  dashboardSummary: [],
  dashboardMatched: null,
  dashboardSummaryLastSuccessKey: null,
  schoolYears: [],
};

export default function reducer(state = initialState, action: Action): State {
  switch (action.type) {
    case FETCH_DASHBOARD_SUMMARY_REQUEST:
      return { ...state, fetching: true, error: null };
    case FETCH_DASHBOARD_SUMMARY_SUCCESS:
      return {
        ...state,
        fetching: false,
        error: null,
        dashboardSummary: action.data,
        dashboardSummaryLastSuccessKey: action.requestKey,
        dashboardMatched:
          action.data && action.data.length
            ? Number(action.data[action.data.length - 1]?.active_schools) || 0
            : 0,
      };
    case FETCH_DASHBOARD_SUMMARY_FAILURE:
      return {
        ...state,
        fetching: false,
        error: action.error,
      };
    case FETCH_SCHOOL_YEARS_REQUEST:
      return {
        ...state,
        schoolYearsFetching: true,
        schoolYears: [],
      };
    case FETCH_SCHOOL_YEARS_SUCCESS:
      return {
        ...state,
        schoolYearsFetching: false,
        schoolYears: action.data,
      };
    case FETCH_SCHOOL_YEARS_FAILURE:
      return {
        ...state,
        schoolYearsFetching: false,
        schoolYears: [],
      };
    default:
      return state;
  }
}

function* fetchSchoolYearsSagaWorker(action: FetchSchoolYearsRequestAction) {
  try {
    const response: AxiosResponse<any> = yield call(
      fetchSchoolYearsApi,
      action.countryId
    );

    yield put({
      type: FETCH_SCHOOL_YEARS_SUCCESS,
      data: normalizeSchoolYears(response.data),
    });
  } catch (error) {
    yield put({ type: FETCH_SCHOOL_YEARS_FAILURE, error });
  }
}

export const fetchDashboardSummary = (
  countryId: number,
  schoolId?: string,
  schoolYear?: string,
  ongoingSchoolYear?: boolean,
  yearMonth?: string,
  adm1?: string,
  adm2?: string,
  adm3?: string,
  adm4?: string
) => ({
  type: FETCH_DASHBOARD_SUMMARY_REQUEST,
  countryId,
  schoolId,
  schoolYear,
  ongoingSchoolYear,
  yearMonth,
  adm1,
  adm2,
  adm3,
  adm4,
});

const normalizeDashboardSummaryKeyPart = (value?: string | boolean) => {
  if (typeof value === "boolean") {
    return value ? "1" : "0";
  }

  return value || "";
};

export const buildDashboardSummaryRequestKey = (
  countryId: number,
  schoolId?: string,
  schoolYear?: string,
  ongoingSchoolYear?: boolean,
  yearMonth?: string,
  adm1?: string,
  adm2?: string,
  adm3?: string,
  adm4?: string
) =>
  [
    String(countryId),
    normalizeDashboardSummaryKeyPart(schoolId),
    normalizeDashboardSummaryKeyPart(schoolYear),
    normalizeDashboardSummaryKeyPart(ongoingSchoolYear),
    normalizeDashboardSummaryKeyPart(yearMonth),
    normalizeDashboardSummaryKeyPart(adm1),
    normalizeDashboardSummaryKeyPart(adm2),
    normalizeDashboardSummaryKeyPart(adm3),
    normalizeDashboardSummaryKeyPart(adm4),
  ].join("|");

export const fetchSchoolYears = (countryId: number) => ({
  type: FETCH_SCHOOL_YEARS_REQUEST,
  countryId,
});

const normalizeSchoolYears = (rawData: any): string[] => {
  const entries = Array.isArray(rawData)
    ? rawData
    : Array.isArray(rawData?.results)
    ? rawData.results
    : [];

  return Array.from(
    new Set(
      entries
        .map((entry: any) => {
          if (typeof entry === "string" || typeof entry === "number") {
            return String(entry);
          }

          if (entry && typeof entry === "object") {
            return (
              entry.school_year ||
              entry.year ||
              entry.value ||
              entry.name ||
              entry.label ||
              ""
            );
          }

          return "";
        })
        .filter(Boolean)
    )
  )
    .sort((a, b) => String(b).localeCompare(String(a)))
    .map((value) => String(value));
};

function fetchDashboardSummaryApi(
  countryId: number,
  schoolId?: string,
  schoolYear?: string,
  ongoingSchoolYear?: boolean,
  yearMonth?: string,
  adm1?: string,
  adm2?: string,
  adm3?: string,
  adm4?: string
) {
  const params = new URLSearchParams({ country_id: String(countryId) });
  const appendCsvAsRepeated = (key: string, value?: string) => {
    const values = String(value || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

    values.forEach((item) => params.append(key, item));
  };

  if (schoolId) {
    appendCsvAsRepeated("school_id", schoolId);
  }

  if (schoolYear) {
    appendCsvAsRepeated("school_year", schoolYear);
  }

  if (ongoingSchoolYear) {
    params.set("ongoing_school_year", "true");
  }

  if (yearMonth) {
    appendCsvAsRepeated("year_month", yearMonth);
  }

  if (adm1) {
    appendCsvAsRepeated("adm_1", adm1);
  }

  if (adm2) {
    appendCsvAsRepeated("adm_2", adm2);
  }

  if (adm3) {
    appendCsvAsRepeated("adm_3", adm3);
  }

  if (adm4) {
    appendCsvAsRepeated("adm_4", adm4);
  }

  const url = `${
    process.env.REACT_APP_API_URL
  }/analytics/dashboard_summary/?${params.toString()}`;

  return axios({ method: "GET", url });
}

function fetchSchoolYearsApi(countryId: number) {
  const params = new URLSearchParams({ country_id: String(countryId) });
  const url = `${
    process.env.REACT_APP_API_URL
  }/analytics/school-years/?${params.toString()}`;

  return axios({ method: "GET", url });
}

function* fetchDashboardSummarySagaWorker(
  action: FetchDashboardSummaryRequestAction
) {
  try {
    const requestKey = buildDashboardSummaryRequestKey(
      action.countryId,
      action.schoolId,
      action.schoolYear,
      action.ongoingSchoolYear,
      action.yearMonth,
      action.adm1,
      action.adm2,
      action.adm3,
      action.adm4
    );

    const response: AxiosResponse<DashboardSummaryResponse> = yield call(
      fetchDashboardSummaryApi,
      action.countryId,
      action.schoolId,
      action.schoolYear,
      action.ongoingSchoolYear,
      action.yearMonth,
      action.adm1,
      action.adm2,
      action.adm3,
      action.adm4
    );
    const normalized = normalizeDashboardSummaryResponse(response.data);
    yield put({
      type: FETCH_DASHBOARD_SUMMARY_SUCCESS,
      data: normalized.data,
      requestKey,
    });
  } catch (error) {
    yield put({ type: FETCH_DASHBOARD_SUMMARY_FAILURE, error });
    yield put(
      errorToast(
        toastFormattedMessages.find((e) => e.name === "toast.infomationError")
          ?.label
      )
    );
  }
}

export function* analyticsSagaWatcher() {
  yield takeLatest(
    FETCH_DASHBOARD_SUMMARY_REQUEST,
    fetchDashboardSummarySagaWorker
  );
  yield takeLatest(FETCH_SCHOOL_YEARS_REQUEST, fetchSchoolYearsSagaWorker);
}

export const getDashboardSummary = (state: RootState) =>
  state.analytics.dashboardSummary;

export const getDashboardMatched = (state: RootState) =>
  state.analytics.dashboardMatched;

export const getDashboardSummaryLastSuccessKey = (state: RootState) =>
  state.analytics.dashboardSummaryLastSuccessKey;

export const getAnalyticsIsFetching = (state: RootState) =>
  state.analytics.fetching;

export const getAnalyticsSchoolYears = (state: RootState) =>
  state.analytics.schoolYears;

export const getAnalyticsSchoolYearsIsFetching = (state: RootState) =>
  state.analytics.schoolYearsFetching;
